import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

/**
 * תבנית פנקס — טבלה פורמלית עם עמודות ברורות וקוים אופקיים.
 * בסגנון ספר רישום / פנקס קהילתי מסודר — מאוד שונה מרשימות חופשיות.
 * רקע שנהב עם כותרות נייבי וגבולות זהב עדינים.
 */

const NAVY = '#1e3a5f';
const GOLD = '#9a7209';
const GOLD_LIGHT = '#c8a84b';
const BORDER = '#d4cfc0';
const BORDER_LIGHT = '#ebe6da';
const BG = '#fcfaf5';
const BG_HEADER = '#f2ede2';
const TEXT_DARK = '#2a2a2a';
const TEXT_MID = '#5a5040';
const TEXT_MUTED = '#9a8f7a';

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- שורת טבלה בודדת -----------------------------------------------------------
const LedgerRow = ({ item, isLast }) => {
  const isHigh = item.priority === 'high';
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 1.5,
      alignItems: 'baseline',
      py: 0.5,
      px: 1.5,
      borderBottom: isLast ? 'none' : `1px solid ${BORDER_LIGHT}`,
      background: isHigh ? 'rgba(154,114,9,0.04)' : 'transparent',
    }}>
      {/* עמודת שם */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.75rem',
          fontWeight: isHigh ? 700 : 500,
          color: isHigh ? NAVY : TEXT_DARK,
          lineHeight: 1.45,
          direction: 'rtl',
        }}>
          {item.label}
          {item.detail && (
            <Typography component="span" sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.6rem',
              color: TEXT_MUTED,
            }}>
              {' '}({item.detail})
            </Typography>
          )}
        </Typography>
      </Box>

      {/* עמודת ערך */}
      {item.value && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.72rem',
          fontWeight: isHigh ? 700 : 500,
          color: isHigh ? GOLD : TEXT_MID,
          lineHeight: 1.4,
          whiteSpace: 'nowrap',
          textAlign: 'left',
          direction: 'rtl',
        }}>
          {item.value}
        </Typography>
      )}
    </Box>
  );
};

// -- כותרת קטגוריה (שורה מודגשת) -----------------------------------------------
const CategoryRow = ({ label }) => (
  <Box sx={{
    background: BG_HEADER,
    borderBottom: `1px solid ${BORDER}`,
    borderTop: `1px solid ${BORDER}`,
    py: 0.35,
    px: 1.5,
  }}>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.56rem',
      fontWeight: 700,
      color: GOLD,
      letterSpacing: '0.1em',
      direction: 'rtl',
    }}>
      ✦ {label}
    </Typography>
  </Box>
);

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => {
  const grouped = groupByCategory(dayData.items);
  return (
    <Box sx={{ mt: 1 }}>
      {/* כותרת יום */}
      <Box sx={{
        background: NAVY,
        py: 0.5,
        px: 1.5,
      }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.68rem',
          fontWeight: 700,
          color: '#fff',
          letterSpacing: '0.05em',
          direction: 'rtl',
        }}>
          {dayData.hebrewDate?.hebrew || dayData.date}
        </Typography>
      </Box>
      {dayData.items.length === 0 ? (
        <Box sx={{ py: 0.75, px: 1.5 }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.65rem',
            color: TEXT_MUTED,
            direction: 'rtl',
          }}>
            אין ענייני יום מיוחדים
          </Typography>
        </Box>
      ) : (
        grouped.map(([cat, catItems]) => (
          <Box key={cat}>
            <CategoryRow label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <LedgerRow key={item.id || `${cat}-${i}`} item={item} isLast={i === catItems.length - 1} />
            ))}
          </Box>
        ))
      )}
    </Box>
  );
};

// == תבנית פנקס ================================================================
const Ledger = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: BG,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      direction: 'rtl',
      overflow: 'hidden',
    }}>
      {/* כותרת עליונה — פס נייבי */}
      <Box sx={{
        background: NAVY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.7,
        flexShrink: 0,
      }}>
        {showTitle && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.68rem',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.1em',
          }}>
            {title}
          </Typography>
        )}
        {hebrewDateStr && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.72rem',
            fontWeight: 700,
            color: GOLD_LIGHT,
          }}>
            {hebrewDateStr}
          </Typography>
        )}
      </Box>

      {/* כותרות עמודות */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 1.5,
        background: BG_HEADER,
        borderBottom: `2px solid ${BORDER}`,
        py: 0.35,
        px: 1.5,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.55rem',
          fontWeight: 700,
          color: TEXT_MUTED,
          letterSpacing: '0.08em',
        }}>
          עניין
        </Typography>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.55rem',
          fontWeight: 700,
          color: TEXT_MUTED,
          letterSpacing: '0.08em',
          textAlign: 'left',
        }}>
          פרט
        </Typography>
      </Box>

      {/* גוף הטבלה */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { background: BORDER, borderRadius: 2 },
      }}>
        {/* loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={18} sx={{ color: NAVY }} />
          </Box>
        )}

        {/* error */}
        {error && (
          <Box sx={{ py: 0.75, px: 1.5 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#c0392b' }}>שגיאה בטעינה</Typography>
          </Box>
        )}

        {/* ריק */}
        {grouped.length === 0 && !loading && (
          <Box sx={{ py: 2, px: 1.5, textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.72rem',
              color: TEXT_MUTED,
            }}>
              אין ענייני יום מיוחדים להיום
            </Typography>
          </Box>
        )}

        {/* שורות לפי קטגוריה */}
        {grouped.map(([cat, catItems]) => (
          <Box key={cat}>
            <CategoryRow label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <LedgerRow key={item.id || `${cat}-${i}`} item={item} isLast={i === catItems.length - 1} />
            ))}
          </Box>
        ))}

        {/* ימי lookahead */}
        {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
          lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))
        )}

        {/* קו סיום תחתון */}
        <Box sx={{
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}60, transparent)`,
          mt: 0.5,
        }} />
      </Box>
    </Box>
  );
};

export default Ledger;
